import { ReactNode, CSSProperties, Ref, MouseEvent } from "react";
import styles from "./XStack.module.css";

export interface XStackProps {
  children?: ReactNode;
  ref?: Ref<HTMLDivElement> | undefined;
  variant?: "button";
  gap?: number | string;
  flexWrap?: "wrap" | "nowrap" | "wrap-reverse";
  padding?: number | string;
  paddingHorizontal?: number | string;
  paddingVertical?: number | string;
  paddingTop?: number | string;
  paddingBottom?: number | string;
  paddingLeft?: number | string;
  paddingRight?: number | string;
  margin?: number | string;
  marginHorizontal?: number | string;
  marginVertical?: number | string;
  marginTop?: number | string;
  marginBottom?: number | string;
  marginLeft?: number | string;
  marginRight?: number | string;
  alignItems?: "flex-start" | "flex-end" | "center" | "stretch" | "baseline";
  justifyContent?:
    | "flex-start"
    | "flex-end"
    | "center"
    | "space-between"
    | "space-around"
    | "space-evenly"
    | "stretch";
  flex?: number | string;
  flexGrow?: number;
  flexShrink?: number;
  width?: number | string;
  height?: number | string;
  minWidth?: number | string;
  minHeight?: number | string;
  maxWidth?: number | string;
  maxHeight?: number | string;
  backgroundColor?: string;
  border?: string;
  borderTop?: string;
  borderBottom?: string;
  borderLeft?: string;
  borderRight?: string;
  borderRadius?: number | string;
  className?: string;
  style?: CSSProperties;
  onPress?: () => void;
  onClick?: (e?: MouseEvent) => void;
}

export default function XStack({
  ref,
  children,
  variant,
  gap,
  flexWrap,
  padding,
  paddingHorizontal,
  paddingVertical,
  paddingTop,
  paddingBottom,
  paddingLeft,
  paddingRight,
  margin,
  marginHorizontal,
  marginVertical,
  marginTop,
  marginBottom,
  marginLeft,
  marginRight,
  alignItems,
  justifyContent,
  flex,
  flexGrow,
  flexShrink,
  width,
  height,
  minWidth,
  minHeight,
  maxWidth,
  maxHeight,
  backgroundColor,
  border,
  borderTop,
  borderBottom,
  borderLeft,
  borderRight,
  borderRadius,
  className = "",
  style,
  onPress,
  onClick,
}: XStackProps) {
  // Handle negative gaps specially - CSS gap doesn't support negative values,
  // so we use margin-left on children instead via the negativeGap class
  const isNegativeGap = typeof gap === "number" && gap < 0;
  const negativeGapClass = isNegativeGap ? styles.negativeGap : "";

  const inlineStyle: CSSProperties = {
    ...(gap !== undefined &&
      !isNegativeGap && {
        gap: typeof gap === "number" ? `var(--spacing-${gap})` : gap,
      }),
    ...(isNegativeGap &&
      ({
        "--xstack-negative-gap": `var(--spacing-${Math.abs(gap)})`,
      } as CSSProperties)),
    ...(flexWrap !== undefined && { flexWrap }),
    ...(padding !== undefined && {
      padding:
        typeof padding === "number" ? `var(--spacing-${padding})` : padding,
    }),
    ...(paddingHorizontal !== undefined && {
      paddingLeft:
        typeof paddingHorizontal === "number"
          ? `var(--spacing-${paddingHorizontal})`
          : paddingHorizontal,
      paddingRight:
        typeof paddingHorizontal === "number"
          ? `var(--spacing-${paddingHorizontal})`
          : paddingHorizontal,
    }),
    ...(paddingVertical !== undefined && {
      paddingTop:
        typeof paddingVertical === "number"
          ? `var(--spacing-${paddingVertical})`
          : paddingVertical,
      paddingBottom:
        typeof paddingVertical === "number"
          ? `var(--spacing-${paddingVertical})`
          : paddingVertical,
    }),
    ...(paddingTop !== undefined && {
      paddingTop:
        typeof paddingTop === "number"
          ? `var(--spacing-${paddingTop})`
          : paddingTop,
    }),
    ...(paddingBottom !== undefined && {
      paddingBottom:
        typeof paddingBottom === "number"
          ? `var(--spacing-${paddingBottom})`
          : paddingBottom,
    }),
    ...(paddingLeft !== undefined && {
      paddingLeft:
        typeof paddingLeft === "number"
          ? `var(--spacing-${paddingLeft})`
          : paddingLeft,
    }),
    ...(paddingRight !== undefined && {
      paddingRight:
        typeof paddingRight === "number"
          ? `var(--spacing-${paddingRight})`
          : paddingRight,
    }),
    ...(margin !== undefined && {
      margin: typeof margin === "number" ? `var(--spacing-${margin})` : margin,
    }),
    ...(marginHorizontal !== undefined && {
      marginLeft:
        typeof marginHorizontal === "number"
          ? `var(--spacing-${marginHorizontal})`
          : marginHorizontal,
      marginRight:
        typeof marginHorizontal === "number"
          ? `var(--spacing-${marginHorizontal})`
          : marginHorizontal,
    }),
    ...(marginVertical !== undefined && {
      marginTop:
        typeof marginVertical === "number"
          ? `var(--spacing-${marginVertical})`
          : marginVertical,
      marginBottom:
        typeof marginVertical === "number"
          ? `var(--spacing-${marginVertical})`
          : marginVertical,
    }),
    ...(marginTop !== undefined && {
      marginTop:
        typeof marginTop === "number"
          ? `var(--spacing-${marginTop})`
          : marginTop,
    }),
    ...(marginBottom !== undefined && {
      marginBottom:
        typeof marginBottom === "number"
          ? `var(--spacing-${marginBottom})`
          : marginBottom,
    }),
    ...(marginLeft !== undefined && {
      marginLeft:
        typeof marginLeft === "number"
          ? `var(--spacing-${marginLeft})`
          : marginLeft,
    }),
    ...(marginRight !== undefined && {
      marginRight:
        typeof marginRight === "number"
          ? `var(--spacing-${marginRight})`
          : marginRight,
    }),
    ...(alignItems !== undefined && { alignItems }),
    ...(justifyContent !== undefined && { justifyContent }),
    ...(flex !== undefined && {
      flex: typeof flex === "number" ? flex.toString() : flex,
    }),
    ...(flexGrow !== undefined && { flexGrow }),
    ...(flexShrink !== undefined && { flexShrink }),
    ...(width !== undefined && {
      width: typeof width === "number" ? `${width}px` : width,
    }),
    ...(height !== undefined && {
      height: typeof height === "number" ? `${height}px` : height,
    }),
    ...(minWidth !== undefined && {
      minWidth: typeof minWidth === "number" ? `${minWidth}px` : minWidth,
    }),
    ...(minHeight !== undefined && {
      minHeight: typeof minHeight === "number" ? `${minHeight}px` : minHeight,
    }),
    ...(maxWidth !== undefined && {
      maxWidth: typeof maxWidth === "number" ? `${maxWidth}px` : maxWidth,
    }),
    ...(maxHeight !== undefined && {
      maxHeight: typeof maxHeight === "number" ? `${maxHeight}px` : maxHeight,
    }),
    ...(backgroundColor !== undefined && { backgroundColor }),
    ...(border !== undefined && { border }),
    ...(borderTop !== undefined && { borderTop }),
    ...(borderBottom !== undefined && { borderBottom }),
    ...(borderLeft !== undefined && { borderLeft }),
    ...(borderRight !== undefined && { borderRight }),
    ...(borderRadius !== undefined && {
      borderRadius:
        typeof borderRadius === "number" ? `${borderRadius}px` : borderRadius,
    }),
    ...style,
  };

  const handleClick = onClick || onPress;

  if (variant === "button") {
    return (
      <button
        type="button"
        className={`${styles.xStack} ${negativeGapClass} ${className}`}
        style={inlineStyle}
        onClick={handleClick}
      >
        {children}
      </button>
    );
  }

  if (handleClick) {
    return (
      <div
        ref={ref}
        className={`${styles.xStack} ${negativeGapClass} ${className}`}
        style={inlineStyle}
        onClick={handleClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleClick();
          }
        }}
      >
        {children}
      </div>
    );
  }

  return (
    <div
      ref={ref}
      className={`${styles.xStack} ${negativeGapClass} ${className}`}
      style={inlineStyle}
    >
      {children}
    </div>
  );
}
